import React, { useState, useEffect, useCallback } from 'react';
import { Layers, X, Plus, Trash2, Edit2, Save, ChevronDown, FileText } from 'lucide-react';

export default function QuoteTemplatePicker({ api, appraisalId, onApplied, currentItemCount }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quote-templates');
      setTemplates(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    if (open || manageOpen) loadTemplates();
  }, [open, manageOpen, loadTemplates]);

  const applyTemplate = async (templateId) => {
    setApplying(templateId);
    try {
      await api.post(`/quote-templates/${templateId}/apply/${appraisalId}`);
      setOpen(false);
      onApplied();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to apply template');
    }
    setApplying(null);
  };

  const saveAsTemplate = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      await api.post(`/quote-templates/save-from-appraisal/${appraisalId}`, {
        name: saveName.trim(),
        description: saveDesc,
      });
      setSaveOpen(false);
      setSaveName('');
      setSaveDesc('');
      loadTemplates();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  return (
    <>
      <div className="relative">
        <button
          data-testid="quote-template-btn"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 text-sm text-indigo-600 font-semibold hover:text-indigo-700 border border-indigo-300 px-3 py-1.5 rounded-md hover:bg-indigo-50"
        >
          <Layers size={14} /> Templates <ChevronDown size={12} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div data-testid="quote-template-dropdown" className="absolute right-0 top-full mt-1 z-50 w-80 bg-white border border-slate-200 rounded-xl shadow-xl">
              <div className="p-3 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-slate-800">Quote Templates</h4>
                  <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Apply a saved set of line items</p>
              </div>

              <div className="max-h-64 overflow-y-auto">
                {loading && <p className="text-xs text-slate-400 text-center py-4">Loading...</p>}
                {!loading && templates.length === 0 && (
                  <div className="text-center py-6 px-4">
                    <FileText size={24} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-xs text-slate-400">No templates yet</p>
                    <p className="text-xs text-slate-400 mt-1">Save your current quote items as a template to reuse later</p>
                  </div>
                )}
                {templates.map(t => (
                  <div key={t._id} data-testid={`template-item-${t._id}`} className="px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
                        {t.description && <div className="text-xs text-slate-400 truncate">{t.description}</div>}
                        <div className="text-xs text-slate-400 mt-0.5">{(t.items || []).length} items</div>
                      </div>
                      <button
                        data-testid={`apply-template-${t._id}`}
                        onClick={() => applyTemplate(t._id)}
                        disabled={applying === t._id}
                        className="ml-2 px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-50 flex-shrink-0"
                      >
                        {applying === t._id ? 'Adding...' : 'Apply'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-2 border-t border-slate-100 flex gap-2">
                {currentItemCount > 0 && (
                  <button
                    data-testid="save-as-template-btn"
                    onClick={() => { setOpen(false); setSaveOpen(true); }}
                    className="flex-1 flex items-center justify-center gap-1 text-xs text-green-600 font-semibold px-2 py-1.5 rounded-md border border-green-200 hover:bg-green-50"
                  >
                    <Save size={12} /> Save Current as Template
                  </button>
                )}
                <button
                  data-testid="manage-templates-btn"
                  onClick={() => { setOpen(false); setManageOpen(true); }}
                  className="flex-1 flex items-center justify-center gap-1 text-xs text-slate-500 font-semibold px-2 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                >
                  <Edit2 size={12} /> Manage
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Save as Template Modal */}
      {saveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div data-testid="save-template-modal" className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800 font-[Manrope]">Save as Template</h3>
              <button onClick={() => setSaveOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500">Save the current {currentItemCount} quote items as a reusable template.</p>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Template Name *</label>
              <input
                data-testid="template-name-input"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="e.g. Standard Re-Roof Package"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
              <input
                data-testid="template-desc-input"
                value={saveDesc}
                onChange={e => setSaveDesc(e.target.value)}
                placeholder="Optional notes about this template"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setSaveOpen(false)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button
                data-testid="confirm-save-template"
                onClick={saveAsTemplate}
                disabled={saving || !saveName.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Templates Modal */}
      {manageOpen && (
        <TemplateManager api={api} templates={templates} onClose={() => setManageOpen(false)} onRefresh={loadTemplates} />
      )}
    </>
  );
}

function TemplateManager({ api, templates, onClose, onRefresh }) {
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', items: [] });
  const [deleting, setDeleting] = useState(null);

  const startEdit = (t) => {
    setEditId(t._id);
    setForm({ name: t.name, description: t.description || '', items: t.items || [] });
  };

  const saveEdit = async () => {
    if (!editId) return;
    try {
      await api.put(`/quote-templates/${editId}`, form);
      setEditId(null);
      onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteTemplate = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/quote-templates/${id}`);
      onRefresh();
    } catch { /* ignore */ }
    setDeleting(null);
  };

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const removeItem = (idx) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { item: '', item_type: '', materials_rate: 0, labour_rate: 0, con_rate: 0, quantity: 1 }] });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div data-testid="manage-templates-modal" className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-800 font-[Manrope]">Manage Quote Templates</h3>
            <p className="text-xs text-slate-400 mt-0.5">{templates.length} templates</p>
          </div>
          <button data-testid="close-manage-templates" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {templates.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">No templates created yet. Save quote items from any appraisal to create your first template.</p>
          )}
          {templates.map(t => (
            <div key={t._id} data-testid={`manage-template-${t._id}`} className="border border-slate-200 rounded-lg overflow-hidden">
              {editId === t._id ? (
                <div className="p-4 space-y-3 bg-indigo-50/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Items ({form.items.length})</label>
                      <button onClick={addItem} className="text-xs text-indigo-600 font-semibold hover:text-indigo-800 flex items-center gap-1"><Plus size={12} /> Add Item</button>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {form.items.map((it, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-1.5 items-center text-xs">
                          <input value={it.item || ''} onChange={e => updateItem(idx, 'item', e.target.value)} placeholder="Category" className="col-span-2 px-1.5 py-1 border border-slate-200 rounded text-xs" />
                          <input value={it.item_type || ''} onChange={e => updateItem(idx, 'item_type', e.target.value)} placeholder="Description" className="col-span-4 px-1.5 py-1 border border-slate-200 rounded text-xs" />
                          <input type="number" step="0.01" value={it.materials_rate || 0} onChange={e => updateItem(idx, 'materials_rate', parseFloat(e.target.value) || 0)} placeholder="Mat$" className="col-span-2 px-1.5 py-1 border border-slate-200 rounded text-xs" />
                          <input type="number" step="0.01" value={it.labour_rate || 0} onChange={e => updateItem(idx, 'labour_rate', parseFloat(e.target.value) || 0)} placeholder="Lab$" className="col-span-2 px-1.5 py-1 border border-slate-200 rounded text-xs" />
                          <input type="number" step="1" value={it.quantity || 1} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} className="col-span-1 px-1.5 py-1 border border-slate-200 rounded text-xs" />
                          <button onClick={() => removeItem(idx)} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center"><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs font-semibold hover:bg-indigo-700"><Save size={12} className="inline mr-1" />Save</button>
                    <button onClick={() => setEditId(null)} className="px-3 py-1.5 border border-slate-200 rounded text-xs text-slate-600 hover:bg-slate-50">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{t.name}</div>
                    {t.description && <div className="text-xs text-slate-400">{t.description}</div>}
                    <div className="text-xs text-slate-400 mt-0.5">{(t.items || []).length} items</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button data-testid={`edit-template-${t._id}`} onClick={() => startEdit(t)} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50"><Edit2 size={14} /></button>
                    <button data-testid={`delete-template-${t._id}`} onClick={() => deleteTemplate(t._id)} disabled={deleting === t._id} className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50 disabled:opacity-50"><Trash2 size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
